/**
 * Боковая панель навигации.
 *
 * 24.10.2026 — Calm Enterprise: группы по задачам, одна открытая группа,
 * ширина 224/60, состояние в localStorage. Не удалять по требованию владельца системы.
 * Модули и testid прежние. CROSS-REF: components/layout/MainLayout.tsx, router.tsx
 *
 * История:
 *  - 15.01.2026 — первый редизайн с тёмным градиентом.
 *  - 16.03.2026 — добавлен пункт OKDoc tracking.
 *  - 30.04.2026 — миграция на shell-* токены, поддержка светлой/тёмной темы.
 *  - 15.05.2026 — демо-режим: скрыть Telegram, Галерея медиа; OKDoc → Лендинги/Переходы.
 *  - 08.10.2026 — чёрный знак MedIntegra и подпись «MT · Модуль уведомлений».
 */
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  MessageSquare, Send, FileText, Users, Settings, LogOut,
  LayoutDashboard, BarChart2, MessageCircle, Smartphone, Link2, UsersRound, Shield, ShieldAlert, ShieldOff, Stethoscope, CalendarDays, UserCircle, MousePointerClick, History, Bot, Image, Activity, PanelLeft,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/utils/cn';
import type { LucideIcon } from 'lucide-react';
import { isDemoNotify as checkDemoNotify } from '@/demo/isDemoNotify';

const isDemoNotify = checkDemoNotify();

const COLLAPSED_KEY = 'mt.notify.sidebar.collapsed';
const GROUP_KEY = 'mt.notify.sidebar.group';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  permission: string | null;
  anyOf?: string[];
  superAdminOnly: boolean;
  testId: string;
  end?: boolean;
  hideInDemo?: boolean;
}

interface NavGroup {
  id: string;
  title: string;
  items: NavItem[];
}

export const navigationGroups: NavGroup[] = [
  {
    id: 'workspace',
    title: 'Рабочее место',
    items: [
      { name: 'Дашборд', href: '/', icon: LayoutDashboard, permission: null, superAdminOnly: false, testId: 'menu-dashboard', end: true },
      { name: 'Входящие', href: '/messages', icon: MessageSquare, permission: 'messages:read', superAdminOnly: false, testId: 'menu-messages' },
      { name: 'Взаимодействия', href: '/interactions', icon: MousePointerClick, permission: 'appointments:read', superAdminOnly: false, testId: 'menu-interactions' },
      { name: 'Очередь рассылок', href: '/broadcast-queue', icon: ShieldAlert, permission: 'campaigns:read', superAdminOnly: false, testId: 'menu-broadcast-queue' },
    ],
  },
  {
    id: 'content',
    title: 'Контент и автоматизация',
    items: [
      { name: 'Подсказки админам', href: '/admin-reply-playbooks', icon: FileText, permission: 'messages:read', superAdminOnly: false, testId: 'menu-admin-reply-playbooks' },
      { name: 'Шаблоны', href: '/templates', icon: FileText, permission: 'templates:read', superAdminOnly: false, testId: 'menu-templates' },
      { name: 'Автоответы', href: '/auto-replies', icon: MessageCircle, permission: 'bot:read', superAdminOnly: false, testId: 'menu-auto-replies' },
      { name: 'Регулярные рассылки', href: '/broadcast-schedules', icon: Send, permission: 'campaigns:read', superAdminOnly: false, testId: 'menu-broadcast-schedules', end: true },
      { name: 'Ручные рассылки', href: '/manual-broadcasts', icon: Send, permission: 'campaigns:read', superAdminOnly: false, testId: 'menu-manual-broadcasts', end: true },
      { name: isDemoNotify ? 'Лендинги' : 'OKDoc лендинги', href: '/okdoc-landings', icon: Link2, permission: 'appointments:read', superAdminOnly: false, testId: 'menu-okdoc-landings', hideInDemo: true },
      { name: isDemoNotify ? 'Переходы' : 'OKDoc переходы', href: '/okdoc-tracking', icon: MousePointerClick, permission: 'appointments:read', superAdminOnly: false, testId: 'menu-okdoc-tracking', hideInDemo: true },
      { name: 'Галерея медиа', href: '/media-library', icon: Image, permission: 'appointments:read', superAdminOnly: false, testId: 'menu-media-library', hideInDemo: true },
    ],
  },
  {
    id: 'data',
    title: 'Данные и настройки',
    items: [
      { name: 'Пациенты', href: '/patients', icon: UserCircle, permission: 'settings:read', superAdminOnly: false, testId: 'menu-patients', hideInDemo: true },
      { name: 'Семьи пациентов', href: '/families', icon: UsersRound, permission: 'families:read', superAdminOnly: false, testId: 'menu-families' },
      { name: 'Каналы уведомлений', href: '/channels-directory', icon: Bot, permission: 'settings:read', superAdminOnly: false, testId: 'menu-channels-directory' },
      { name: 'Умные фолбэки', href: '/settings', icon: Settings, permission: 'settings:read', superAdminOnly: false, testId: 'menu-smart-fallbacks' },
      { name: 'История отправок', href: '/manual-broadcasts/history', icon: History, permission: 'campaigns:read', superAdminOnly: false, testId: 'menu-broadcast-history' },
      { name: 'Записи', href: '/appointments', icon: CalendarDays, permission: 'appointments:read', superAdminOnly: false, testId: 'menu-appointments', hideInDemo: true },
      { name: 'Рассылки', href: '/campaigns', icon: Send, permission: 'campaigns:read', superAdminOnly: false, testId: 'menu-campaigns' },
      { name: 'Услуги', href: '/services', icon: Stethoscope, permission: 'services:read', superAdminOnly: false, testId: 'menu-services', hideInDemo: true },
      { name: 'Пользователи', href: '/users', icon: Users, permission: 'users:read', superAdminOnly: false, testId: 'menu-users' },
      { name: 'Аналитика', href: '/analytics', icon: BarChart2, permission: 'analytics:read', superAdminOnly: false, testId: 'menu-analytics' },
      { name: 'Telegram бот (старое)', href: '/bot-settings', icon: MessageCircle, permission: 'bot:read', superAdminOnly: false, testId: 'menu-bot-settings', hideInDemo: true },
      { name: 'Клиенты мессенджеров', href: '/messenger-clients', icon: Smartphone, permission: 'settings:read', anyOf: ['settings:read', 'bot:read', 'messenger_clients:manage'], superAdminOnly: false, testId: 'menu-messenger-clients', hideInDemo: true },
      { name: 'Привязка номеров', href: '/phone-binding', icon: Link2, permission: 'settings:write', superAdminOnly: false, testId: 'menu-phone-binding' },
      { name: 'Чёрный список', href: '/blacklist', icon: ShieldOff, permission: 'settings:write', superAdminOnly: false, testId: 'menu-blacklist' },
      { name: 'Балансы и подписки', href: '/billing-monitor', icon: ShieldAlert, permission: null, superAdminOnly: true, testId: 'menu-billing-monitor' },
      { name: 'Heartbeat', href: '/heartbeat', icon: Activity, permission: null, superAdminOnly: true, testId: 'menu-heartbeat' },
      { name: 'Администрирование', href: '/admin', icon: Shield, permission: null, superAdminOnly: true, testId: 'menu-admin' },
    ],
  },
];

export function resolveOpenNavGroup(pathname: string, groups: NavGroup[] = navigationGroups): string {
  let best: { id: string; length: number } | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      const matches = item.href === '/'
        ? pathname === '/'
        : pathname === item.href || pathname.startsWith(`${item.href}/`);
      if (matches && (!best || item.href.length > best.length)) {
        best = { id: group.id, length: item.href.length };
      }
    }
  }
  return best?.id || 'workspace';
}

function readStoredGroup(): string | null {
  if (typeof window === 'undefined') return 'workspace';
  const stored = window.localStorage.getItem(GROUP_KEY);
  if (stored === 'none') return null;
  return stored || 'workspace';
}

export function Sidebar() {
  const { user, logout } = useAuth();
  const { can, canAny } = usePermissions();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  });
  const [openGroup, setOpenGroup] = useState<string | null>(readStoredGroup);

  const visibleGroups = navigationGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => {
        if (isDemoNotify && item.hideInDemo) return false;
        if (item.superAdminOnly) return !user?.tenant_id;
        if (item.anyOf && item.anyOf.length > 0) return canAny(...item.anyOf);
        return item.permission === null || can(item.permission);
      }),
    }))
    .filter((group) => group.items.length > 0);

  useEffect(() => {
    setOpenGroup(resolveOpenNavGroup(location.pathname, visibleGroups));
  }, [location.pathname]);

  useEffect(() => {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  }, [collapsed]);

  useEffect(() => {
    window.localStorage.setItem(GROUP_KEY, openGroup || 'none');
  }, [openGroup]);

  return (
    <aside className={cn('shell-sidebar', collapsed && 'shell-sidebar-collapsed')}>
      <div className={cn('flex items-center gap-2 px-3 pt-3', collapsed ? 'justify-center' : 'justify-between')}>
        <div className={cn('shell-sidebar-brand min-w-0', collapsed && 'mb-0 justify-center')}>
          <div className="flex h-8 w-8 shrink-0 items-center justify-center" aria-hidden>
            <svg className="h-[18px] w-[18px] text-shell-strong" viewBox="0 0 128 128" fill="currentColor">
              <path d="M101.8 85.9L64 20L36.1 68.8H45.7L64 36.8L86.9 76.3H52.9V76.4H31.4L26.2 85.9H101.8Z" />
            </svg>
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-tight text-shell-strong">MT Notify</p>
              <p className="truncate text-[11px] leading-4 text-shell-muted">Модуль уведомлений</p>
            </div>
          )}
        </div>
        {!collapsed && (
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-shell-muted hover:bg-shell-hover hover:text-shell-text"
            title="Свернуть меню"
            onClick={() => setCollapsed(true)}
          >
            <PanelLeft className="h-[18px] w-[18px]" />
          </button>
        )}
      </div>
      {collapsed && (
        <button
          type="button"
          className="mx-auto mb-2 inline-flex h-8 w-8 items-center justify-center rounded-lg text-shell-muted hover:bg-shell-hover hover:text-shell-text"
          title="Развернуть меню"
          onClick={() => setCollapsed(false)}
        >
          <PanelLeft className="h-[18px] w-[18px]" />
        </button>
      )}

      <nav className="min-h-0 flex-1 space-y-3 overflow-y-auto px-2 pb-3">
        {visibleGroups.map((group) => {
          const expanded = collapsed || openGroup === group.id;
          return (
            <div key={group.id} className="shell-sidebar-section">
              {!collapsed && (
                <button
                  type="button"
                  className="shell-sidebar-title flex w-full items-center justify-between rounded-lg px-3 py-1 text-left hover:text-shell-text"
                  onClick={() => setOpenGroup((current) => (current === group.id ? null : group.id))}
                  aria-expanded={openGroup === group.id}
                >
                  <span>{group.title}</span>
                  <span className="text-[10px]">{openGroup === group.id ? '–' : '+'}</span>
                </button>
              )}
              {expanded && group.items.map((item) => (
                <NavLink
                  key={item.testId}
                  to={item.href}
                  end={item.end}
                  data-testid={item.testId}
                  title={item.name}
                  className={({ isActive }) => cn('shell-sidebar-link', isActive && 'shell-sidebar-link-active', collapsed && 'justify-center px-0')}
                >
                  <item.icon className="h-[18px] w-[18px] shrink-0" />
                  {!collapsed && <span className="truncate">{item.name}</span>}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>

      <div className="mt-auto border-t border-shell-border p-3">
        <div className={cn('flex items-center gap-2', collapsed && 'flex-col')}>
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-shell-primary-soft text-sm font-semibold text-shell-primary-strong">
            {user?.full_name?.charAt(0) || 'U'}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-shell-text">{user?.full_name}</p>
              <p className="truncate text-xs text-shell-muted">{user?.role_name}</p>
            </div>
          )}
          <button
            onClick={logout}
            className="rounded-lg p-2 text-shell-muted hover:bg-shell-danger-soft hover:text-shell-danger"
            data-testid="btn-logout"
            title="Выйти"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
